import { Badge, Group, Paper, Stack, Switch, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconMeeple } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type AccessRule, type ContentOption } from '@creativitas/shared';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/StateViews';
import { toUserMessage } from '@/lib/api';
import { AccessRulesEditor } from './AccessRulesEditor';
import { ADMIN_GAMES_KEY, fetchAdminGames, setAccessRules, setGameStatus } from './content-api';

/**
 * Игры в администрировании (ТЗ демо, п. 3.2).
 *
 * Демоверсия и полная игра — две записи, и правила доступа у каждой свои:
 * изменение одной не трогает другую и не трогает индивидуальные права. Здесь
 * же — публикация (демо появляется черновиком).
 *
 * Блоков готовности демо и формы адресов предложения здесь больше нет
 * (19.10.2026): хватает ли карт, сервер проверяет сам при создании партии, а
 * кнопки в конце демо ведут на общий адрес из «Продажи → Тарифы и оплата».
 */
export function AdminGamesPage() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ADMIN_GAMES_KEY, queryFn: fetchAdminGames });
  const items = query.data?.items ?? [];
  const fullGames: ContentOption[] = items
    .filter((item) => item.variant === 'full')
    .map((item) => ({
      kind: 'game',
      id: item.id,
      title: item.title,
      slug: item.slug,
      status: item.status,
      accessRules: item.accessRules,
    }));

  const fail = (error: unknown) => notifications.show({ color: 'red', message: toUserMessage(error) });
  const access = useMutation({
    mutationFn: (input: { gameId: string; rules: AccessRule[]; requiredGameId: string | null }) =>
      setAccessRules('game', input.gameId, input.rules, input.requiredGameId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_GAMES_KEY });
      notifications.show({ message: 'Правила доступа сохранены', color: 'teal' });
    },
    onError: fail,
  });
  const status = useMutation({
    mutationFn: (input: { gameId: string; published: boolean }) =>
      setGameStatus(input.gameId, input.published ? 'published' : 'draft'),
    onSuccess: (result) => queryClient.setQueryData(ADMIN_GAMES_KEY, result),
    onError: fail,
  });

  return (
    <>
      <PageHeader
        eyebrow="Администрирование"
        title="Игры"
        description="Доступ к демоверсии и к полной игре настраивается отдельно. Поле и колоды у них общие."
      />
      {query.isLoading ? <LoadingState label="Загружаем игры..." /> : query.isError ? (
        <ErrorState onRetry={() => void query.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState icon={<IconMeeple size={28} />} title="Игр пока нет" />
      ) : (
        <Stack gap="lg">
          {items.map((game) => (
            <Paper key={game.id} withBorder p="lg" data-testid={`admin-game-${game.slug}`}>
              <Stack gap="md">
                <Group justify="space-between" align="flex-start">
                  <div>
                    <Group gap="xs">
                      <Title order={3}>{game.title}</Title>
                      <Badge color={game.variant === 'demo' ? 'gold' : 'teal'} variant="light">
                        {game.variant === 'demo' ? 'Демоверсия' : 'Полная игра'}
                      </Badge>
                    </Group>
                    <Text size="sm" c="var(--text-muted)">/games/{game.slug}</Text>
                  </div>
                  <Switch
                    label="Опубликована в каталоге"
                    checked={game.status === 'published'}
                    disabled={status.isPending}
                    onChange={(event) =>
                      status.mutate({ gameId: game.id, published: event.currentTarget.checked })}
                  />
                </Group>

                <Stack gap="xs">
                  <Text fw={600}>Кому доступно</Text>
                  <AccessRulesEditor
                    value={game.accessRules}
                    requiredGameId={game.accessRequiredGameId}
                    games={fullGames}
                    saving={access.isPending && access.variables?.gameId === game.id}
                    onSave={(rules, requiredGameId) => access.mutate({ gameId: game.id, rules, requiredGameId })}
                  />
                  {game.variant === 'demo' ? (
                    <Text size="xs" c="var(--text-subtle)">
                      Чтобы владельцы полной игры видели демо, оставьте «Владельцам игры» с полной
                      игрой. Права полной игры отсюда не меняются.
                    </Text>
                  ) : null}
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </>
  );
}
